import { RATE_CARD_PENDING_UPLOAD_STATUSES, RATE_CARD_UPLOAD_STATUSES } from './constants';
import type { IRateCardShow, IRateCardUpload } from './types';

/**
 * Which of the detail page's faces to show. The backend stores no draft flag — status is derived from
 * `effective_from` plus supersession — so the phase is inferred from uploads plus published lines.
 */
export const RATE_CARD_PHASES = {
  /** The card exists but nothing has been uploaded to it yet. */
  EMPTY: 'empty',
  IN_PROGRESS: 'in_progress',
  VALIDATION_FAILED: 'validation_failed',
  VALIDATED: 'validated',
  PUBLISHED: 'published',
} as const;

export type RateCardPhase = (typeof RATE_CARD_PHASES)[keyof typeof RATE_CARD_PHASES];

/**
 * True when one side came back with something to correct. Both halves matter: the importer sets the
 * status, a non-zero `unresolved_lines` is what blocks a publish, and the two must not disagree.
 */
export const isFailedUpload = (upload: IRateCardUpload): boolean =>
  upload.status?.id === RATE_CARD_UPLOAD_STATUSES.PROCESSED_WITH_ERRORS || upload.unresolved_lines > 0;

export const hasFailedUpload = (card: IRateCardShow | null): boolean => (card?.uploads ?? []).some(isFailedUpload);

/** True while the importer still has work to do, which is what the detail page polls on. */
export const hasPendingUpload = (card: IRateCardShow | null): boolean =>
  (card?.uploads ?? []).some(upload => RATE_CARD_PENDING_UPLOAD_STATUSES.includes(upload.status?.id));

/**
 * `linesTotal` is the grid's `totalCount`, or `null` while unknown. Only the last distinction needs it,
 * and a *failed* probe leaves it null for good — gating everything on it left an import with no phase.
 */
export const deriveRateCardPhase = (card: IRateCardShow | null, linesTotal: number | null): RateCardPhase | null => {
  if (!card) return null;

  // An import in flight outranks lines from an earlier publish: publishing again over a live card is
  // supported, so reading the card as settled would hide the import running on it.
  if (hasPendingUpload(card)) return RATE_CARD_PHASES.IN_PROGRESS;

  // Decidable without the count: lines come only from an upload and none can be removed, so a card with
  // no uploads was never published. This is the phase a reader most needs when the probe is what failed.
  if (card.uploads.length === 0) return RATE_CARD_PHASES.EMPTY;

  // Published or merely validated is the one question the count answers, so it alone waits for it.
  if (linesTotal === null) return null;

  if (linesTotal > 0) return RATE_CARD_PHASES.PUBLISHED;

  return hasFailedUpload(card) ? RATE_CARD_PHASES.VALIDATION_FAILED : RATE_CARD_PHASES.VALIDATED;
};
